import { type ReactNode, type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

type RequestWindowOptions = {
  width?: number;
  height?: number;
  copyStyles?: boolean;
};

export type UsePictureInPicture = {
  isSupported: boolean;
  isActive: boolean;
  open: (options?: RequestWindowOptions) => Promise<Window | null>;
  close: () => void;
  PipPortal: FC<{ children: ReactNode }>;
};

function copyStylesToWindow(targetDocument: Document) {
  const sourceDocument = document;
  Array.from(sourceDocument.querySelectorAll<HTMLLinkElement>("link[rel=stylesheet]")).forEach(
    (linkEl) => {
      const cloned = targetDocument.createElement("link");
      cloned.rel = "stylesheet";
      cloned.href = linkEl.href;
      targetDocument.head.appendChild(cloned);
    },
  );

  Array.from(sourceDocument.querySelectorAll<HTMLStyleElement>("style")).forEach((styleEl) => {
    const cloned = targetDocument.createElement("style");
    cloned.textContent = styleEl.textContent;
    targetDocument.head.appendChild(cloned);
  });
}

export const usePictureInPicture = (): UsePictureInPicture => {
  const isSupported = useMemo(
    () => window.isSecureContext && "documentPictureInPicture" in window,
    [],
  );

  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pagehideHandlerRef = useRef<(() => void) | null>(null);
  const styleObserverRef = useRef<MutationObserver | null>(null);

  useEffect(
    () => () => {
      if (pipWindow && !pipWindow.closed) {
        if (pagehideHandlerRef.current) {
          pipWindow.removeEventListener("pagehide", pagehideHandlerRef.current);
        }
        pipWindow.close();
      }
    },
    [pipWindow],
  );

  const open = useCallback(
    async (options?: RequestWindowOptions) => {
      if (!isSupported) {
        return null;
      }

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const win: Window = await (window as any).documentPictureInPicture.requestWindow({
          width: options?.width ?? 420,
          height: options?.height ?? window.outerHeight,
        });

        const root = win.document.createElement("div");
        root.id = "pip-root";
        root.style.width = "100%";
        root.style.height = "100%";
        root.style.display = "flex";
        root.style.flexDirection = "column";
        root.style.background = "transparent";
        win.document.body.style.margin = "0";
        win.document.body.appendChild(root);

        if (options?.copyStyles !== false) {
          copyStylesToWindow(win.document);

          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              for (const node of mutation.addedNodes) {
                if (node instanceof HTMLStyleElement) {
                  const cloned = win.document.createElement("style");
                  cloned.textContent = node.textContent;
                  win.document.head.appendChild(cloned);
                } else if (node instanceof HTMLLinkElement && node.rel === "stylesheet") {
                  const cloned = win.document.createElement("link");
                  cloned.rel = "stylesheet";
                  cloned.href = node.href;
                  win.document.head.appendChild(cloned);
                }
              }
            }
          });
          observer.observe(document.head, { childList: true });
          styleObserverRef.current = observer;
        }

        const pagehideHandler = () => {
          styleObserverRef.current?.disconnect();
          styleObserverRef.current = null;
          setPipWindow(null);
          containerRef.current = null;
        };
        pagehideHandlerRef.current = pagehideHandler;
        win.addEventListener("pagehide", pagehideHandler);

        containerRef.current = root;
        setPipWindow(win);
        return win;
      } catch (error) {
        console.error("Failed to open Picture-in-Picture window", error);
        return null;
      }
    },
    [isSupported],
  );

  const close = useCallback(() => {
    styleObserverRef.current?.disconnect();
    styleObserverRef.current = null;
    if (pipWindow && !pipWindow.closed) {
      if (pagehideHandlerRef.current) {
        pipWindow.removeEventListener("pagehide", pagehideHandlerRef.current);
      }
      pipWindow.close();
    }
    setPipWindow(null);
    containerRef.current = null;
  }, [pipWindow]);

  const PipPortal = useCallback(({ children }: { children: ReactNode }) => {
    if (!containerRef.current) {
      return null;
    }
    return createPortal(children, containerRef.current);
  }, []);

  return {
    isSupported,
    isActive: !!pipWindow,
    open,
    close,
    PipPortal,
  };
};
